'use client'

import { toast } from '@/components/common/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { TextArea } from '@/components/ui/textarea'
import { useUserQuery } from '@/hooks/react-query/user/use-user'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { TTheMartianWish } from '@/types/the-martian'
import { ArrowRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { THE_MARTIAN_WISH_LIMITS } from '../_actions/wishes.constants'
import {
  useSendTheMartianWishMutation,
  useTheMartianWishes,
} from '../_hooks/use-the-martian-wishes'

const NAME_MAX_LENGTH = THE_MARTIAN_WISH_LIMITS.name
const MESSAGE_MAX_LENGTH = THE_MARTIAN_WISH_LIMITS.message
const CLOUD_ROWS = 4
// Mỗi hàng cần đủ dài để chạy marquee không bị hở trên màn rộng
const MIN_CHIPS_PER_ROW = 10

// Figma: cỡ chữ desktop 20 → 68px, mobile thu nhỏ ~25% (giữ tối thiểu 8px cho dễ đọc)
const CHIP_SIZES = [
  'text-[8px] lg:text-[20px]',
  'text-[9px] lg:text-[36px]',
  'text-[8px] lg:text-[28px]',
  'text-[14px] lg:text-[56px]',
  'text-[9px] lg:text-[36px]',
  'text-[11px] lg:text-[46px]',
  'text-[8px] lg:text-[24px]',
  'text-[15px] lg:text-[64px]',
  'text-[9px] lg:text-[36px]',
  'text-[16px] lg:text-[68px]',
]
const CHIP_COLORS = ['text-[#2F5ACF]', 'text-[#111213]', 'text-[#F9C81A]']

type FormValues = {
  name: string
  message: string
}

const buildCloudRows = (wishes: TTheMartianWish[]) => {
  if (!wishes.length) return []
  return Array.from({ length: CLOUD_ROWS }, (_, row) => {
    const length = Math.max(MIN_CHIPS_PER_ROW, wishes.length)
    // Lệch điểm bắt đầu mỗi hàng để các hàng không giống nhau
    return Array.from({ length }, (_, i) => {
      const index = (i + row * 3) % wishes.length
      return { wish: wishes[index], style: (i * 7 + row * 3) % 10 }
    })
  })
}

type TheMartianWishesProps = {
  /** Lời chúc lấy từ server action lúc render trang */
  initialWishes: TTheMartianWish[]
}

export const TheMartianWishes = ({ initialWishes }: TheMartianWishesProps) => {
  const t = useTranslations('the_martian_hardcode.wishes')
  const { data: wishes = initialWishes } = useTheMartianWishes(initialWishes)
  const { mutateAsync: sendWish, isPending } = useSendTheMartianWishMutation()
  const [selected, setSelected] = useState<TTheMartianWish | null>(null)

  const rows = useMemo(() => buildCloudRows(wishes), [wishes])
  const activeWish = selected ?? wishes[0]

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { name: '', message: '' } })

  // Khách đã đăng nhập: điền sẵn họ tên (không ghi đè nếu khách đã tự nhập)
  const { data: user } = useUserQuery()
  const userFullname = user?.fullname?.trim().slice(0, NAME_MAX_LENGTH) ?? ''

  useEffect(() => {
    if (userFullname && !getValues('name')) {
      setValue('name', userFullname)
    }
  }, [userFullname, getValues, setValue])

  const onSubmit = handleSubmit(async (values) => {
    const body = { name: values.name.trim(), message: values.message.trim() }
    try {
      await sendWish(body)
      // Giữ lại họ tên của khách đã đăng nhập để gửi tiếp lời chúc khác
      reset({ name: userFullname, message: '' })
      // Hiển thị ngay lời chúc vừa gửi trong lúc danh sách được tải lại
      setSelected({ id: 'local-new-wish', ...body })
      toast.message({
        title: t('success_title'),
        message: t('success_message'),
      })
    } catch {
      toast.message({ title: t('error_title'), message: t('error_message') })
    }
  })

  // Chưa có lời chúc nào (hoặc chưa được duyệt): vẫn hiện tiêu đề + form, ẩn word cloud
  const hasWishes = wishes.length > 0

  return (
    <section className='relative isolate grid gap-6 overflow-hidden bg-black px-5 py-10 lg:gap-10 lg:px-15 lg:py-20'>
      <Image
        src={ImageHelper.getImageUrlCdn(
          '/image/2026/10/19/tm_black_symbol.png'
        )}
        alt=''
        aria-hidden
        width={1262}
        height={1209}
        className='absolute left-[calc(50%-727px)] top-1/2 -z-10 w-[1262px] max-w-none -translate-x-1/2 -translate-y-1/2 max-lg:hidden'
      />

      <div className='grid gap-5 lg:flex lg:items-center lg:justify-between'>
        <h2 className='max-w-[300px] font-criteria text-2xl font-semibold uppercase leading-[35px] text-white lg:max-w-[581px] lg:text-4xl lg:leading-[47px]'>
          {t('title')}
        </h2>
        <p className='text-sm text-white lg:text-right lg:text-lg lg:leading-6'>
          {t('description_line1')}
          <br />
          {t('description_line2')}
        </p>
      </div>

      {/* Word cloud: các hàng tên chạy ngang, chạm vào tên để đọc lời chúc */}
      {hasWishes ? (
        <div className='group relative grid gap-2 overflow-hidden rounded-md border-[0.25px] border-[#E4E4E4] pb-[6%] pt-[10.7%] lg:gap-6 lg:rounded-3xl lg:border lg:pb-10 lg:pt-16'>
          {rows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className={cn(
                'flex w-max items-center group-hover:[animation-play-state:paused]',
                rowIndex % 2 ? 'animate-marqueeX-reverse' : 'animate-marqueeX'
              )}
              style={{ animationDuration: `${60 + rowIndex * 8}s` }}
            >
              {[0, 1].map((copy) => (
                <div
                  key={copy}
                  className='flex shrink-0 items-center gap-[7px] pr-[7px] lg:gap-[30px] lg:pr-[30px]'
                  aria-hidden={copy === 1 ? 'true' : undefined}
                >
                  {row.map(({ wish, style }, index) => (
                    <button
                      key={`${wish.id}-${index}`}
                      type='button'
                      tabIndex={copy === 1 ? -1 : undefined}
                      onClick={() => setSelected(wish)}
                      className={cn(
                        'whitespace-nowrap rounded-[3px] bg-white px-[3px] py-0.5 text-center leading-normal shadow-[0_0_2px_rgba(0,0,0,0.06),4px_4px_13.5px_rgba(0,0,0,0.12)] transition-transform hover:scale-105 lg:rounded-xl lg:px-3.5 lg:py-2.5',
                        CHIP_SIZES[style],
                        CHIP_COLORS[style % CHIP_COLORS.length],
                        activeWish?.id === wish.id &&
                          'ring-1 ring-[#F56A24] lg:ring-2'
                      )}
                    >
                      {wish.name}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : null}

      <div
        className={cn(
          'grid gap-6 lg:gap-10',
          activeWish ? 'lg:grid-cols-2' : 'lg:max-w-[880px]'
        )}
      >
        {activeWish ? (
          <div className='flex flex-col gap-4 rounded-2xl bg-neutral-700 p-3 lg:min-h-[300px] lg:p-6'>
            <p className='font-criteria text-base font-bold uppercase leading-6 tracking-[-0.02em] text-[#F56A24] lg:text-lg lg:leading-[26px]'>
              {activeWish?.name}
            </p>
            <p className='break-words text-base leading-6 text-white lg:flex-1 lg:text-lg'>
              {activeWish?.message}
            </p>
            {hasWishes ? (
              <p className='text-xs leading-4 text-neutral-400 lg:text-sm lg:leading-5'>
                {t('hint')}
              </p>
            ) : null}
          </div>
        ) : null}

        <form onSubmit={onSubmit} className='flex flex-col gap-3' noValidate>
          <Input
            size='lg'
            label={t('name_label')}
            placeholder={t('name_placeholder')}
            maxLength={NAME_MAX_LENGTH}
            className='h-[49px] font-normal placeholder:text-base placeholder:font-normal placeholder:text-neutral-300 lg:h-[54px]'
            error={errors.name}
            {...register('name', {
              validate: (value) => !!value.trim() || t('name_required'),
              maxLength: {
                value: NAME_MAX_LENGTH,
                message: t('max_length', { max: NAME_MAX_LENGTH }),
              },
            })}
          />
          <TextArea
            label={t('message_label')}
            placeholder={t('message_placeholder')}
            maxLength={MESSAGE_MAX_LENGTH}
            rows={4}
            className='h-[108px] resize-none border-neutral-300 placeholder:text-neutral-300 lg:h-[122px]'
            error={errors.message}
            {...register('message', {
              validate: (value) => !!value.trim() || t('message_required'),
              maxLength: {
                value: MESSAGE_MAX_LENGTH,
                message: t('max_length', { max: MESSAGE_MAX_LENGTH }),
              },
            })}
          />
          <Button
            type='submit'
            variant='white'
            loading={isPending}
            className='h-[37px] w-full text-sm lg:h-[50px] lg:w-fit lg:px-10 lg:text-base'
          >
            {t('submit')}
            <ArrowRight className='!size-4 lg:!size-5' />
          </Button>
        </form>
      </div>
    </section>
  )
}
